(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-cart-drawer';
  var ROUTES = {
    cartAdd: '/cart/add.js',
    cartChange: '/cart/change.js',
    cartGet: '/cart.js'
  };

  // Recurring/subscription icon shown before the chosen frequency (white stroke,
  // sits on a coloured badge). Kept in sync with the section Liquid markup.
  var RECURRING_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">' +
    '<path d="M2.72563 4.94252C3.46675 3.66345 4.64746 2.6973 6.0479 2.22397C7.44835 1.75064 8.97309 1.8024 10.3382 2.3696C11.7034 2.9368 12.8158 3.98078 13.4685 5.30717C14.1212 6.63356 14.2696 8.15194 13.8861 9.5796C13.5026 11.0073 12.6133 12.2469 11.3838 13.0677C10.1544 13.8885 8.66849 14.2345 7.2029 14.0413C5.7373 13.8481 4.39187 13.1288 3.41711 12.0174C2.44236 10.9061 1.90472 9.47832 1.9043 8.00005" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M5.71363 4.9524H2.66602V1.90479" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  // Approx delivery interval in days parsed from a selling plan name
  // ("Every 6 weeks" -> 42, "Every 1 month" -> 30). Used to sort frequencies
  // shortest-first. Falls back to a large number so unparseable names sort last.
  function sellingPlanIntervalDays(name) {
    var s = String(name || '').toLowerCase();
    var per = 7;
    if (s.indexOf('month') !== -1) per = 30;
    else if (s.indexOf('year') !== -1) per = 365;
    else if (s.indexOf('day') !== -1) per = 1;
    else if (s.indexOf('week') !== -1) per = 7;
    else return 1e9;
    var m = s.match(/\d+/);
    var num = m ? parseInt(m[0], 10) : 1;
    return num * per;
  }

  // ---------------------------------------------------------------------------
  // Active currency resolution (markets / multi-currency aware)
  // ---------------------------------------------------------------------------
  var activeCurrency = null;

  function getActiveCurrency() {
    if (activeCurrency) return activeCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  }

  function formatMoney(cents) {
    var currency = getActiveCurrency();
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  // ---------------------------------------------------------------------------
  // Internal state shared across the page
  // ---------------------------------------------------------------------------
  var sections = [];
  var latestCart = null;
  var currentFocusedSection = null;

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getFocusableElements(root) {
    if (!root) return [];
    return Array.prototype.slice.call(
      root.querySelectorAll(
        'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]'
      )
    ).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function dispatchCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  // ---------------------------------------------------------------------------
  // Cart AJAX API
  // ---------------------------------------------------------------------------
  function getCart() {
    return fetch(ROUTES.cartGet, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res.ok) throw new Error('Failed to fetch cart');
      return res.json();
    });
  }

  function addToCart(variantId, quantity, properties, sellingPlanId) {
    quantity = quantity == null ? 1 : quantity;
    properties = properties || {};
    var body = { id: Number(variantId), quantity: quantity };
    if (properties && Object.keys(properties).length) {
      body.properties = properties;
    }
    if (sellingPlanId) {
      body.selling_plan = Number(sellingPlanId);
    }
    return fetch(ROUTES.cartAdd, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw err;
          });
        }
        return res.json();
      })
      .then(function () {
        return getCart();
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function addMultipleToCart(items) {
    return fetch(ROUTES.cartAdd, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw err;
          });
        }
        return res.json();
      })
      .then(function () {
        return getCart();
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return fetch(ROUTES.cartChange, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineItemKey, quantity: quantity })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw err;
          });
        }
        return res.json();
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
    return fetch(ROUTES.cartChange, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineItemKey, selling_plan: sellingPlanId })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw err;
          });
        }
        return res.json();
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  // Reset a subscription line back to a one-time purchase (drop the selling plan).
  function resetLineItemToOneTime(lineItemKey) {
    return fetch(ROUTES.cartChange, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineItemKey, selling_plan: null })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw err;
          });
        }
        return res.json();
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function refreshCart() {
    return getCart().then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  function buildLineItemHTML(item) {
    var imageHtml = '';
    if (item.image) {
      var img = item.image;
      // Use Shopify image transform (append _160x via filter pattern)
      var src = img;
      if (typeof img === 'string' && img.indexOf('?') === -1) {
        src = img.replace(/(\.[a-zA-Z]+)(\?.*)?$/, '_160x$1$2');
      }
      imageHtml =
        '<img class="oddit-cart-drawer__item-image" ' +
        'src="' + escapeHtml(src) + '" ' +
        'alt="' + escapeHtml(item.product_title || item.title || '') + '" ' +
        'loading="lazy" width="80" height="80" />';
    }

    var variantTitle = '';
    if (item.variant_title && item.variant_title !== 'Default Title') {
      variantTitle =
        '<p class="oddit-cart-drawer__item-variant">' +
        escapeHtml(item.variant_title) +
        '</p>';
    }

    var productTitle = item.product_title || item.title || '';
    var lineKey = item.key;

    var hasSellingPlan = item.selling_plan_allocation != null;
    var currentPlanId = (hasSellingPlan && item.selling_plan_allocation.selling_plan)
      ? String(item.selling_plan_allocation.selling_plan.id || '')
      : '';
    var currentPlanName = (hasSellingPlan && item.selling_plan_allocation.selling_plan)
      ? (item.selling_plan_allocation.selling_plan.name || '')
      : '';

    // Subscription frequency control. Mirrors the server render in the section Liquid.
    //  - OTP line: "Subscribe & Save" with a transparent <select> overlaid, so a click
    //    on it opens the native dropdown. A hidden, selected placeholder means ANY pick
    //    fires `change` and converts (native selects don't fire when the current value
    //    is re-picked).
    //  - Subscription line: the chosen frequency + a return icon BUTTON that resets the
    //    line to a one-time purchase. No select here — clicking the frequency does nothing.
    var planNameHtml = '';
    var planMap = window.OdditSellingPlanMap || {};
    var plans = planMap[item.product_id] || planMap[String(item.product_id)];
    if (plans && plans.length) {
      plans = plans.slice().sort(function (a, b) {
        return sellingPlanIntervalDays(a.name) - sellingPlanIntervalDays(b.name);
      });
    }
    if (hasSellingPlan) {
      planNameHtml =
        '<div class="oddit-cart-drawer__freq oddit-cart-drawer__freq--active" data-line-item-subscribe>' +
          '<span class="oddit-cart-drawer__freq-label" data-line-item-plan>' +
            escapeHtml(currentPlanName) +
            '<button type="button" class="oddit-cart-drawer__freq-reset" data-line-item-reset data-line-item-key="' +
              escapeHtml(item.key) + '" aria-label="Switch to one-time purchase">' +
              '<span class="oddit-cart-drawer__freq-icon" aria-hidden="true">' + RECURRING_ICON_SVG + '</span>' +
            '</button>' +
          '</span>' +
        '</div>';
    } else if (plans && plans.length) {
      var optsHtml = plans.map(function (p) {
        return '<option value="' + escapeHtml(p.id) + '">' + escapeHtml(p.name) + '</option>';
      }).join('');
      planNameHtml =
        '<div class="oddit-cart-drawer__freq" data-line-item-subscribe>' +
          '<span class="oddit-cart-drawer__freq-label" data-line-item-plan>Subscribe &amp; Save</span>' +
          '<select class="oddit-cart-drawer__freq-select" data-switch-frequency data-line-item-key="' +
            escapeHtml(item.key) + '" aria-label="Delivery frequency">' +
            '<option value="" selected disabled hidden>Subscribe &amp; Save</option>' +
            optsHtml +
          '</select>' +
        '</div>';
    }

    // Shopify's `final_line_price` doesn't reliably include selling-plan adjustments
    // (varies by subscription app). Fall back to `selling_plan_allocation.price × qty`
    // when that's lower so the discount surfaces either way.
    var linePriceDisplay = item.final_line_price;
    var compareLinePrice = item.original_line_price;
    if (hasSellingPlan && item.selling_plan_allocation.price != null) {
      var subscriptionTotal = Number(item.selling_plan_allocation.price) * Number(item.quantity);
      if (subscriptionTotal < linePriceDisplay) linePriceDisplay = subscriptionTotal;
      if (item.original_price != null) {
        var variantCompare = Number(item.original_price) * Number(item.quantity);
        if (variantCompare > compareLinePrice) compareLinePrice = variantCompare;
      }
    }
    var showCompare = compareLinePrice != null
      && linePriceDisplay != null
      && compareLinePrice > linePriceDisplay;
    var compareHtml = showCompare
      ? '<s class="oddit-cart-drawer__item-price-compare" data-line-item-compare>'
        + formatMoney(compareLinePrice)
        + '</s>'
      : '';

    var discountsHtml = '';
    var allocations = item.line_level_discount_allocations || item.discount_allocations || [];
    if (allocations.length) {
      var discountItems = '';
      for (var d = 0; d < allocations.length; d++) {
        var allocAmount = allocations[d].amount;
        var origPrice = item.original_line_price;
        var badgeText = '';
        if (allocAmount && origPrice) {
          var pct = Math.round(allocAmount * 100 / origPrice);
          if (pct > 0) badgeText = 'Save ' + pct + '%';
        }
        if (badgeText) {
          discountItems += '<li class="oddit-cart-drawer__item-discount-badge">' + escapeHtml(badgeText) + '</li>';
        }
      }
      if (discountItems) {
        discountsHtml = '<ul class="oddit-cart-drawer__item-discounts" data-line-item-discounts>' + discountItems + '</ul>';
      }
    }

    return (
      '<article class="oddit-cart-drawer__item" data-line-item data-line-item-key="' +
      escapeHtml(lineKey) +
      '" data-product-id="' +
      escapeHtml(item.product_id) +
      '">' +
        '<a href="' + escapeHtml(item.url || '#') + '" class="oddit-cart-drawer__item-image-wrap" aria-hidden="true" tabindex="-1">' +
          imageHtml +
        '</a>' +
        '<div class="oddit-cart-drawer__item-details">' +
          '<div class="oddit-cart-drawer__item-heading">' +
            '<h3 class="oddit-cart-drawer__item-name">' +
              '<a href="' + escapeHtml(item.url || '#') + '">' + escapeHtml(productTitle) + '</a>' +
            '</h3>' +
            planNameHtml +
          '</div>' +
          variantTitle +
          '<div class="oddit-cart-drawer__item-pricing">' +
            '<p class="oddit-cart-drawer__item-price" data-line-item-price>' +
              formatMoney(linePriceDisplay) +
            '</p>' +
            compareHtml +
          '</div>' +
          discountsHtml +
          '<div class="oddit-cart-drawer__item-actions">' +
            '<div class="oddit-cart-drawer__quantity" role="group" aria-label="Quantity selector">' +
              '<button class="oddit-cart-drawer__quantity-btn oddit-cart-drawer__quantity-btn--minus" ' +
                'type="button" ' +
                'aria-label="Decrease quantity of ' + escapeHtml(productTitle) + '" ' +
                'data-quantity-decrease ' +
                'data-quantity-action="decrease" ' +
                'data-line-key="' + escapeHtml(lineKey) + '" ' +
                'data-line-item-key="' + escapeHtml(lineKey) + '">' +
                '&ndash;' +
              '</button>' +
              '<span class="oddit-cart-drawer__quantity-value" ' +
                'aria-live="polite" ' +
                'data-quantity-display ' +
                'data-line-item-key="' + escapeHtml(lineKey) + '">' +
                escapeHtml(item.quantity) +
              '</span>' +
              '<button class="oddit-cart-drawer__quantity-btn oddit-cart-drawer__quantity-btn--plus" ' +
                'type="button" ' +
                'aria-label="Increase quantity of ' + escapeHtml(productTitle) + '" ' +
                'data-quantity-increase ' +
                'data-quantity-action="increase" ' +
                'data-line-key="' + escapeHtml(lineKey) + '" ' +
                'data-line-item-key="' + escapeHtml(lineKey) + '">' +
                '+' +
              '</button>' +
            '</div>' +
            '<button class="oddit-cart-drawer__remove" ' +
              'type="button" ' +
              'data-remove-item ' +
              'data-remove-line ' +
              'data-line-key="' + escapeHtml(lineKey) + '" ' +
              'data-line-item-key="' + escapeHtml(lineKey) + '" ' +
              'aria-label="Remove ' + escapeHtml(productTitle) + ' from cart">' +
              'Remove' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</article>'
    );
  }

  function renderCart(cart) {
    if (!cart) return;

    // Cache currency from the cart for accurate, market-aware formatting
    if (cart.currency) {
      activeCurrency = cart.currency;
    }
    latestCart = cart;

    sections.forEach(function (section) {
      // 1. Render line items
      var itemsContainers = section.querySelectorAll('[data-cart-items]');
      var itemsHtml = (cart.items || []).map(buildLineItemHTML).join('');
      itemsContainers.forEach(function (container) {
        container.innerHTML = itemsHtml;
      });

      // 2. Update count
      var counts = section.querySelectorAll('.oddit-cart-drawer__count, [data-cart-count], .oddit-cart-drawer__subtotal-count');
      counts.forEach(function (el) {
        el.textContent = cart.item_count;
      });

      // 3. Update subtotal
      var subtotals = section.querySelectorAll('[data-cart-subtotal]');
      subtotals.forEach(function (el) {
        el.textContent = formatMoney(cart.total_price);
      });

      // 4. Progress bar
      updateProgressBar(section, cart);

      // 5. Toggle cart states (empty vs filled) — do NOT touch
      // [data-cart-additional-checkout] or its ancestors; we only flip display.
      var filledEl = section.querySelector('[data-cart-filled]');
      var emptyEl = section.querySelector('[data-cart-empty]');
      var footerEl = section.querySelector('[data-cart-footer]');
      var promotionEl = section.querySelector('[data-shipping-progress]');
      var isEmpty = !cart.item_count || cart.item_count === 0;

      if (filledEl) filledEl.style.display = isEmpty ? 'none' : '';
      if (emptyEl) emptyEl.style.display = isEmpty ? '' : 'none';
      if (footerEl) footerEl.style.display = isEmpty ? 'none' : '';
      if (promotionEl) promotionEl.style.display = isEmpty ? 'none' : '';

      // Generic [data-cart-state] support if present
      var stateEls = section.querySelectorAll('[data-cart-state]');
      stateEls.forEach(function (el) {
        var state = el.getAttribute('data-cart-state');
        var shouldShow =
          (state === 'empty' && isEmpty) ||
          (state === 'filled' && !isEmpty) ||
          (state === 'has-items' && !isEmpty);
        el.style.display = shouldShow ? '' : 'none';
      });

      // 6. Filter upsells: hide ones whose product_id is already in the cart
      var inCartProductIds = (cart.items || []).map(function (i) {
        return String(i.product_id);
      });
      var upsells = section.querySelectorAll('[data-upsell-offer], [data-product-id]');
      upsells.forEach(function (el) {
        // Only target upsell/cross-sell cards (not line items)
        if (el.hasAttribute('data-line-item')) return;
        var pid = el.getAttribute('data-product-id');
        if (!pid) {
          // Find variant button inside and resolve product via DOM data
          var btn = el.querySelector('[data-add-to-cart][data-product-id]');
          if (btn) pid = btn.getAttribute('data-product-id');
        }
        if (pid && inCartProductIds.indexOf(String(pid)) !== -1) {
          el.style.display = 'none';
        } else if (el.matches('[data-upsell-offer]') || el.matches('.oddit-cart-drawer__upsell, .oddit-cart-drawer__rec')) {
          el.style.display = '';
        }
      });

      // 6b. Hide the upsell container when every upsell inside is hidden.
      var upsellContainers = section.querySelectorAll('[data-upsells]');
      upsellContainers.forEach(function (container) {
        var cards = container.querySelectorAll('[data-upsell-offer]');
        var hasVisible = false;
        for (var c = 0; c < cards.length; c++) {
          if (cards[c].style.display !== 'none') {
            hasVisible = true;
            break;
          }
        }
        container.style.display = hasVisible ? '' : 'none';
      });
    });

    // 7. Notify other sections (header cart count badge, etc.)
    dispatchCartUpdated(cart);
  }

  // ---------------------------------------------------------------------------
  // Progress bar
  // ---------------------------------------------------------------------------
  function updateProgressBar(section, cart) {
    var thresholdRaw = section.getAttribute('data-free-shipping-threshold');
    var threshold = parseFloat(thresholdRaw);
    if (!threshold || threshold <= 0) return;

    var cartTotal = (cart.total_price || 0) / 100;
    var percentage = Math.min((cartTotal / threshold) * 100, 100);
    var remaining = Math.max(threshold - cartTotal, 0);

    // Width fills — support both spec naming and actual Liquid naming
    var fills = section.querySelectorAll(
      '.oddit-cart-drawer__shipping-fill, .oddit-cart-drawer__progress-fill, [data-progress-fill]'
    );
    fills.forEach(function (el) {
      el.style.width = percentage + '%';
    });

    // Move the icon to the current progress position
    var icons = section.querySelectorAll('[data-progress-icon]');
    icons.forEach(function (el) {
      el.style.left = percentage + '%';
    });

    // ARIA on progressbar
    var bars = section.querySelectorAll('[data-cart-progress-bar]');
    bars.forEach(function (el) {
      el.setAttribute('aria-valuenow', Math.round(percentage));
    });

    // Shipping text content
    var texts = section.querySelectorAll(
      '.oddit-cart-drawer__shipping-text, .oddit-cart-drawer__promotion-text, [data-shipping-text]'
    );
    var template = section.getAttribute('data-shipping-progress-text') || '';
    var successTemplate = section.getAttribute('data-shipping-success-text') || '';
    texts.forEach(function (el) {
      if (remaining <= 0) {
        if (successTemplate) {
          el.innerHTML = successTemplate;
        } else if (!el.dataset.successText) {
          // Keep server-rendered text if no template hint
          el.innerHTML = el.innerHTML;
        }
      } else {
        if (template) {
          el.innerHTML = template.replace(/\[amount\]|\{\{\s*amount\s*\}\}/g, formatMoney(remaining * 100));
        } else {
          // Replace any existing currency string heuristically: rebuild only the
          // amount placeholder if present in the stored attribute
          var stored = el.getAttribute('data-progress-template');
          if (stored) {
            el.innerHTML = stored.replace(/\[amount\]|\{\{\s*amount\s*\}\}/g, formatMoney(remaining * 100));
          }
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Per-section initialization
  // ---------------------------------------------------------------------------
  function initSection(section) {
    if (!section || section.dataset.odditInit === 'true') return;
    section.dataset.odditInit = 'true';

    // Reveal hidden root (was display:none to prevent FOUC)
    if (section.style.display === 'none') {
      section.style.display = '';
    }

    sections.push(section);

    var controller = new AbortController();
    var signal = controller.signal;
    section._odditAbortController = controller;

    var overlay = section.querySelector('[data-cart-overlay], .oddit-cart-drawer__overlay');
    var closeBtn = section.querySelector('[data-cart-close], .oddit-cart-drawer__close');
    var panel = section.querySelector('.oddit-cart-drawer__panel');
    var itemsContainer = section.querySelector('[data-cart-items]');
    var lastFocused = null;

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      if (!section.classList.contains('is-open')) return;
      var focusables = getFocusableElements(panel || section);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function open() {
      lastFocused = document.activeElement;
      section.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      currentFocusedSection = section;
      // Focus first interactive element
      setTimeout(function () {
        var focusables = getFocusableElements(panel || section);
        var target = closeBtn || focusables[0];
        if (target && typeof target.focus === 'function') {
          target.focus();
        }
      }, 50);
      // Refresh cart silently on open
      refreshCart().catch(function () {
        /* non-fatal */
      });
    }

    function close() {
      section.classList.remove('is-open');
      document.body.style.overflow = '';
      if (currentFocusedSection === section) currentFocusedSection = null;
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus(); } catch (e) { /* noop */ }
      }
    }

    function toggle() {
      if (section.classList.contains('is-open')) {
        close();
      } else {
        open();
      }
    }

    // Cache progress templates from DOM so re-renders use fresh strings
    var promotionTextEl = section.querySelector('[data-shipping-text]');
    if (promotionTextEl && !promotionTextEl.getAttribute('data-progress-template')) {
      promotionTextEl.setAttribute('data-progress-template', promotionTextEl.innerHTML.trim());
    }

    // Close handlers
    if (closeBtn) {
      closeBtn.addEventListener('click', close, { signal: signal });
    }
    if (overlay) {
      overlay.addEventListener('click', close, { signal: signal });
    }

    // Keyboard: escape + focus trap
    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key === 'Escape' && section.classList.contains('is-open')) {
          close();
        } else if (e.key === 'Tab') {
          trapFocus(e);
        }
      },
      { signal: signal }
    );

    // External open/close triggers
    document.addEventListener('cart-drawer:open', open, { signal: signal });
    document.addEventListener('cart-drawer:close', close, { signal: signal });

    // -------------------------------------------------------------------------
    // Event delegation for quantity controls and remove buttons
    // -------------------------------------------------------------------------
    if (itemsContainer) {
      itemsContainer.addEventListener(
        'click',
        function (e) {
          var decreaseBtn = e.target.closest('[data-quantity-decrease], [data-quantity-action="decrease"]');
          var increaseBtn = e.target.closest('[data-quantity-increase], [data-quantity-action="increase"]');
          var removeBtn = e.target.closest('[data-remove-item], [data-remove-line]');

          if (decreaseBtn) {
            e.preventDefault();
            var dKey = decreaseBtn.getAttribute('data-line-item-key') || decreaseBtn.getAttribute('data-line-key');
            if (!dKey) return;
            var item = (latestCart && latestCart.items) ?
              latestCart.items.find(function (i) { return i.key === dKey; }) : null;
            var currentQty = item ? item.quantity : 1;
            var newQty = Math.max(currentQty - 1, 0);
            decreaseBtn.disabled = true;
            updateLineItemQuantity(dKey, newQty)
              .catch(function (err) { console.error('Cart update failed:', err); })
              .then(function () { decreaseBtn.disabled = false; });
            return;
          }

          if (increaseBtn) {
            e.preventDefault();
            var iKey = increaseBtn.getAttribute('data-line-item-key') || increaseBtn.getAttribute('data-line-key');
            if (!iKey) return;
            var iitem = (latestCart && latestCart.items) ?
              latestCart.items.find(function (i) { return i.key === iKey; }) : null;
            var iCurrent = iitem ? iitem.quantity : 1;
            increaseBtn.disabled = true;
            updateLineItemQuantity(iKey, iCurrent + 1)
              .catch(function (err) { console.error('Cart update failed:', err); })
              .then(function () { increaseBtn.disabled = false; });
            return;
          }

          if (removeBtn) {
            e.preventDefault();
            var rKey = removeBtn.getAttribute('data-line-item-key') || removeBtn.getAttribute('data-line-key');
            if (!rKey) return;
            removeBtn.disabled = true;
            removeFromCart(rKey)
              .catch(function (err) { console.error('Cart remove failed:', err); })
              .then(function () { removeBtn.disabled = false; });
          }
        },
        { signal: signal }
      );
    }

    // -------------------------------------------------------------------------
    // Upsell / cross-sell add-to-cart buttons (delegated at section level so
    // upsells re-rendered into a state container also work)
    // -------------------------------------------------------------------------
    section.addEventListener(
      'click',
      function (e) {
        var addBtn = e.target.closest(
          '[data-add-to-cart][data-variant-id], ' +
          '.oddit-cart-drawer__upsell-add[data-variant-id], ' +
          '.oddit-cart-drawer__rec-add[data-variant-id], ' +
          '.oddit-cart-drawer__upsell-cta[data-variant-id]'
        );
        if (addBtn) {
          e.preventDefault();
          var variantId = addBtn.getAttribute('data-variant-id');
          if (!variantId) return;
          addBtn.disabled = true;
          addBtn.classList.add('is-loading');
          var upsellContainer = addBtn.closest('[data-upsell-discount-code]');
          var discountCode = upsellContainer
            ? upsellContainer.getAttribute('data-upsell-discount-code')
            : null;
          addToCart(Number(variantId), 1)
            .then(function () {
              if (discountCode) {
                return fetch('/discount/' + encodeURIComponent(discountCode), {
                  method: 'GET',
                  credentials: 'same-origin',
                  redirect: 'manual',
                }).catch(function () {});
              }
            })
            .catch(function (err) {
              console.error('Add to cart failed:', err);
            })
            .then(function () {
              addBtn.disabled = false;
              addBtn.classList.remove('is-loading');
            });
          return;
        }

      },
      { signal: signal }
    );

    // -------------------------------------------------------------------------
    // Subscription frequency picker — convert an OTP line to a subscription, or
    // change the frequency of an existing subscription line, when the overlay
    // <select> changes.
    // -------------------------------------------------------------------------
    section.addEventListener(
      'change',
      function (e) {
        var freqSelect = e.target.closest('[data-switch-frequency]');
        if (!freqSelect) return;
        var planId = freqSelect.value;
        var lineKey = freqSelect.getAttribute('data-line-item-key');
        if (!planId || !lineKey) return;
        freqSelect.disabled = true;
        updateLineItemToSubscription(lineKey, planId)
          .catch(function (err) {
            console.error('Subscription switch failed:', err);
          })
          .then(function () {
            freqSelect.disabled = false;
          });
      },
      { signal: signal }
    );

    // -------------------------------------------------------------------------
    // Return icon on a subscription line — reset it back to a one-time purchase.
    // -------------------------------------------------------------------------
    section.addEventListener(
      'click',
      function (e) {
        var resetBtn = e.target.closest('[data-line-item-reset]');
        if (!resetBtn) return;
        e.preventDefault();
        var lineKey = resetBtn.getAttribute('data-line-item-key');
        if (!lineKey) return;
        resetBtn.disabled = true;
        resetLineItemToOneTime(lineKey)
          .catch(function (err) {
            console.error('Reset to one-time failed:', err);
          })
          .then(function () {
            resetBtn.disabled = false;
          });
      },
      { signal: signal }
    );

    // -------------------------------------------------------------------------
    // Checkout button — navigate to /checkout
    // -------------------------------------------------------------------------
    var checkoutBtn = section.querySelector('[data-cart-checkout], .oddit-cart-drawer__checkout-cta, .oddit-cart-drawer__checkout');
    if (checkoutBtn) {
      checkoutBtn.addEventListener(
        'click',
        function (e) {
          // The button lives inside <form action="/cart"> with name="checkout"
          // so Shopify's native form post normally handles this. We still
          // expose explicit JS navigation as a fallback per spec.
          if (checkoutBtn.tagName === 'BUTTON' && checkoutBtn.getAttribute('type') === 'submit') {
            // Let the native form submission run; do not preventDefault.
            return;
          }
          e.preventDefault();
          window.location.href = '/checkout';
        },
        { signal: signal }
      );
    }

    // Listen for cart:updated dispatched by other places (e.g. PDP add)
    document.addEventListener(
      'cart:updated',
      function (e) {
        if (e && e.detail && e.detail.cart) {
          if (e.detail.cart === latestCart) return;
          latestCart = e.detail.cart;
        }
      },
      { signal: signal }
    );

    // Prime cart state silently so subsequent qty +/- has access to quantities
    getCart()
      .then(function (cart) {
        latestCart = cart;
        if (cart && cart.currency) activeCurrency = cart.currency;
      })
      .catch(function () { /* non-fatal */ });

    // Stash per-section API to support theme-editor reload teardown
    section._oddit = {
      open: open,
      close: close,
      toggle: toggle
    };
  }

  function destroySection(section) {
    if (!section) return;
    if (section._odditAbortController) {
      section._odditAbortController.abort();
      section._odditAbortController = null;
    }
    var idx = sections.indexOf(section);
    if (idx !== -1) sections.splice(idx, 1);
    section.dataset.odditInit = '';
  }

  // ---------------------------------------------------------------------------
  // Public API helpers — operate on the first registered section (typically
  // there is only one cart drawer on a page)
  // ---------------------------------------------------------------------------
  function open() {
    var section = sections[0];
    if (section && section._oddit) section._oddit.open();
  }
  function close() {
    var section = sections[0];
    if (section && section._oddit) section._oddit.close();
  }
  function toggle() {
    var section = sections[0];
    if (section && section._oddit) section._oddit.toggle();
  }

  // ---------------------------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------------------------
  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });

  // ---------------------------------------------------------------------------
  // Public API — PascalCase: window.OdditCartDrawer
  // ---------------------------------------------------------------------------
  window.OdditCartDrawer = {
    open: open,
    close: close,
    toggle: toggle,
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart,
    renderCart: renderCart,
    formatMoney: formatMoney
  };
})();